/**
 * PushManager -- Manages push notification registration and handling.
 *
 * Uses NativeService to bridge Capacitor PushNotifications (native FCM/APNS)
 * and Web Push API (PWA) for cross-platform notification support.
 */

import { NativeService } from './NativeService';

export interface PushRegistration {
  token: string;
  platform: 'web' | 'android' | 'ios';
  registeredAt: number;
}

export interface PushPayload {
  title: string;
  body: string;
  icon?: string;
  data?: Record<string, unknown>;
  actionUrl?: string;
}

export interface WebPushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export class AweniPushManager {
  private token: string | null = null;
  private handlers: Array<(payload: PushPayload) => void> = [];

  async register(): Promise<PushRegistration | null> {
    const granted = await NativeService.requestPushPermission();
    if (!granted) return null;

    const token = await NativeService.registerPush();
    if (!token) return null;

    this.token = token;

    // On native, listen for incoming push notifications
    if (NativeService.isNative()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          const payload: PushPayload = {
            title: notification.title || '',
            body: notification.body || '',
            data: notification.data,
          };
          for (const handler of this.handlers) {
            handler(payload);
          }
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = action.notification.data;
          if (data?.actionUrl) {
            window.location.href = data.actionUrl as string;
          }
        });
      } catch {
        // Plugin not available
      }
    }

    return {
      token,
      platform: NativeService.getPlatform(),
      registeredAt: Date.now(),
    };
  }

  async unregister(): Promise<void> {
    this.token = null;
  }

  onNotification(handler: (payload: PushPayload) => void): void {
    this.handlers.push(handler);
  }

  offNotification(handler: (payload: PushPayload) => void): void {
    this.handlers = this.handlers.filter(h => h !== handler);
  }

  getToken(): string | null {
    return this.token;
  }

  getWebSubscriptionPayload(): WebPushSubscriptionPayload | null {
    if (!this.token) return null;
    try {
      const parsed = JSON.parse(this.token) as Partial<WebPushSubscriptionPayload>;
      const endpoint = typeof parsed.endpoint === 'string' ? parsed.endpoint : '';
      const p256dh = typeof parsed.keys?.p256dh === 'string' ? parsed.keys.p256dh : '';
      const auth = typeof parsed.keys?.auth === 'string' ? parsed.keys.auth : '';
      if (!endpoint || !p256dh || !auth) return null;
      return {
        endpoint,
        keys: { p256dh, auth },
      };
    } catch {
      return null;
    }
  }
}
